import Box from "@mui/material/Box";
import {ColDef, ICellRendererParams, IServerSideGetRowsParams} from "ag-grid-community";
import {AgGridReact} from "ag-grid-react";
import * as React from "react";
import {useCallback, useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import SectionHeader from "../../../../util/component/SectionHeader";
import {exceptionListsAtom} from "../../state/exceptionListsRecoil";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {fromGenericBeToGenericFe, onGridReady, useActionColDef, resolveFilterCodes} from "../../utils";
import {DEFAULT_COL_DEF, SET_SNACKBAR} from "../../constants";
import {useExceptionListRepository} from "../../api/repositories/ExceptionListRepository";
import {DialogInterface, FormDialog, SelectElementModelBE, SelectElementModelFE} from "../../api/types/generic";
import * as yup from "yup";
import {
    useExceptionListAncillaryExceptionRepository
} from "../../api/repositories/ExceptionListAncillaryExceptionRepository";
import {useAgGridLocale} from "../../../../config/globalConfig";
import {Button, Dialog, DialogActions, DialogContent, Grid, useTheme} from "@mui/material";
import Loading from "../../common/components/Loading";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import AddIcon from "@mui/icons-material/Add";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import BootstrapDialogTitle from "../../common/components/BootstrapDialogTitle";
import {ExceptionListEntityResponse} from "../../api/types/exceptionList";
import BasicToolbar from "../../common/components/BasicToolbar";
import FormExceptionListAncillaryException
    from "./FormExceptionListAncillaryException/FormExceptionListAncillaryException";
import ExceptionListAncillaryExceptionFilters from "./Filters/ExceptionListAncillaryExceptionFilters";
import DeleteActionIcon from "../../common/components/DeleteActionIcon";
import DialogComponent from "../../common/components/DialogComponent";
import {selectedExceptionListAncillaryExceptionAtom,} from "../../state/exceptionListAncillaryExceptionsRecoil";
import {
    ExceptionListAncillaryExceptionAssociation,
    ExceptionListAncillaryExceptions,
    FormExceptionListAncillaryExceptionModel,
    FormSearchExceptionListAncillaryExceptionModel,
} from "../../api/types/exceptionListAncillaryExceptionModel";
import {AncillaryExceptionEntityResponse, AncillaryExceptionSelectFE,} from "../../api/types/ancillaryException";
import {ancillaryExceptionSelectAtom} from "../../state/ancillaryExceptionRecoil";
import {useMutation} from "@tanstack/react-query";

const ExceptionListAncillaryException = () => {
    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    // Use Ref
    const exceptionListGridRef = useRef<AgGridReact>(null);
    const ancillaryExceptionGridRef = useRef<AgGridReact>(null);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Recoil
    const [, setExceptionLists] = useRecoilState(exceptionListsAtom);
    const [, setAncillaryExceptions] = useRecoilState(ancillaryExceptionSelectAtom);
    const [selectedExceptionListAncillaryException, setSelectedExceptionListAncillaryException] = useRecoilState(selectedExceptionListAncillaryExceptionAtom);

    // Use Repository Hooks
    const {getExceptionLists} = useExceptionListRepository();
    const {
        postExceptionListAncillaryExceptionAll,
        getExceptionListAncillaryExceptionsPaged,
        deleteExceptionListAncillaryException,
        getDeleteException
    } = useExceptionListAncillaryExceptionRepository();

    // Use State
    const [formDialog, setFormDialog] = useState<FormDialog | null>(null);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);
    const [exceptionListInUse, setExceptionListInUse] = useState<boolean>(false);

    const handleGetStaticContent = async () => {
        try {
            const exceptionLists: ExceptionListEntityResponse[] =
                await getExceptionLists();
            setExceptionLists(
                fromGenericBeToGenericFe(exceptionLists).sort((a, b) =>
                    a.label.localeCompare(b.label)
                )
            );
        } catch (e) {
            dispatchGlobalData(SET_SNACKBAR.ERROR);
        }
    };

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [testFilters, setTestFilters] = useState<FormSearchExceptionListAncillaryExceptionModel>({
        exceptionListName: "",
        exceptionName: "",
        dealers: dealerFilteredOptions,
        legalEntities: everyLegalEntitiesWithoutAll
    })

    // Use Effect
    useEffect(() => {
        handleGetStaticContent();
        // eslint-disable-next-line
    }, []);

    /* AG GRID - START */
    // Use Mutation
    const mutationDeleteExceptionListAncillaryException = useMutation({
        mutationFn: (id: number) =>
            getDeleteException({
                exceptionListId:
                    selectedExceptionListAncillaryException?.exceptionList.id || 0,
                ancillaryExceptionId: id,
            }),
        onSuccess: () => {
            handleGetExceptionListAncillaryExceptionsPage();
        },
    });

    const handleGetExceptionListAncillaryExceptionsPage = () => {
        exceptionListGridRef.current!.api.refreshServerSide({purge: true});
    };

    const handleTriggerFilters = (entries: FormSearchExceptionListAncillaryExceptionModel) => {
        const hardcodedFilter = Object.entries(entries).reduce((acc, [key, value]) => ({
            ...acc,
            [key]: {type: "contains", filter: value}
        }), {});

        exceptionListGridRef.current?.api.paginationGoToFirstPage();
        exceptionListGridRef.current?.api.setFilterModel(hardcodedFilter);
        exceptionListGridRef.current?.api.onFilterChanged();
        setSelectedExceptionListAncillaryException(null);
        exceptionListGridRef?.current?.api?.deselectAll();
    };

    useEffect(() => {
        if (exceptionListGridRef?.current) {
            handleTriggerFilters({
                exceptionListName:
                testFilters.exceptionListName,
                exceptionName: testFilters.exceptionName,
                legalEntities: resolveFilterCodes(testFilters.legalEntities, everyLegalEntitiesWithoutAll),
                dealers: resolveFilterCodes(testFilters.dealers, dealerFilteredOptions),
            });
        }
    }, []);

    useEffect(() => {
        handleTriggerResetFilters()
    }, [])

    const handleTriggerResetFilters = () => {
        // LPP-24637: dealer e legalEntity sono obbligatori per il servizio di pricing.
        // Il reset deve ripristinare i valori del profilo utente (come lo stato iniziale),
        // non svuotarli: con le liste vuote la ricerca viene rifiutata con 400 "Validation failure".
        setTestFilters({
            exceptionListName: "",
            exceptionName: "",
            dealers: dealerFilteredOptions,
            legalEntities: everyLegalEntitiesWithoutAll
        });
        handleTriggerFilters({
            exceptionListName: "",
            exceptionName: "",
            dealers: dealerFilteredOptions,
            legalEntities: everyLegalEntitiesWithoutAll
        });
    };

    const handleTriggerFiltersInitial = () => {
        handleTriggerFilters({
            exceptionListName:
            testFilters.exceptionListName,
            exceptionName: testFilters.exceptionName,
            legalEntities: resolveFilterCodes(testFilters.legalEntities, everyLegalEntitiesWithoutAll),
            dealers: resolveFilterCodes(testFilters.dealers, dealerFilteredOptions),
        });
    };

    // useEffect(() => {
    //     setTimeout(() => {
    //         handleTriggerFiltersInitial();
    //     }, 500);
    // }, [i18n.resolvedLanguage]);

    const exceptionListColumnDefs: ColDef[] = [
        {
            headerName: t("pricing:exceptionList.table.headers.exceptionList"),
            headerTooltip: t("pricing:exceptionList.table.headers.exceptionList"),
            field: "exceptionListName",
            cellRenderer: (params: any) => params.data.exceptionList?.name,
            flex: 1,
            filter: "text"
        },
        {
            field: "legalEntities",
            headerName: t(
                "pricing:rate.form.labels.mainDefinitionLabels.legalEntity"
            ),
            headerTooltip: t(
                "pricing:rate.form.labels.mainDefinitionLabels.legalEntities"
            ),
            editable: false,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:legalEntities.'.concat(params.data.exceptionList.legalEntity)),
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            field: "dealers",
            headerName: t("pricing:rate.form.labels.mainDefinitionLabels.dealer"),
            headerTooltip: t("pricing:rate.form.labels.mainDefinitionLabels.dealer"),
            editable: false,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:dealers.'.concat(params.data.exceptionList.dealer)),
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            field: "exceptionName",
            hide: true,
            filter: "text"
        }
    ];

    const {actionColDef} = useActionColDef();
    const ancillaryExceptionColumnDefs: ColDef[] = [
        {
            headerName: t("pricing:exceptionList.table.headers.ancillaryExceptions"),
            headerTooltip: t("pricing:exceptionList.table.headers.ancillaryExceptions"),
            field: "name",
            flex: 1
        },
        {
            ...actionColDef,
            cellRenderer: (params: ICellRendererParams) => {
                return !!user.profiles.find((profile: any) => {
                    return profile.auths.find((auth: any) => {
                        return auth.name === "SCOPE_PRC_DELETE_EXCEPTION_LIST_ANCILLARY_EXCEPTION"
                    })
                }) ? <DeleteActionIcon params={params}
                                       action={deleteButtonClicked}
                                       styles={{
                                           margin: "auto",
                                           color: theme.palette.accent.main
                                       }}
                /> : undefined;
            }
        }
    ];

    const defaultColDef = DEFAULT_COL_DEF;

    const createDatasource = () => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let sort =
                    params.request.sortModel.length === 0
                        ? ""
                        : params.request.sortModel[0].colId;
                let order =
                    params.request.sortModel.length === 0
                        ? ""
                        : params.request.sortModel[0].sort;

                const {filterModel} = params.request;

                const filter: FormSearchExceptionListAncillaryExceptionModel = Object.entries(
                    filterModel as Record<string, { filter?: string; type: string }>
                ).reduce((acc, [key, value]) => ({
                    ...acc,
                    [key]: value?.filter || ""
                }), testFilters);
                sort = sort === "exceptionListName" ? "exceptionList_description" : sort;


                const exceptionListAncillaryExceptionsPaged = await getExceptionListAncillaryExceptionsPaged({
                    ...filter,
                    // dealers: filteredDealersOpts,
                    // legalEntities:
                    //     (filter.legalEntities as string[])?.length === 0
                    //         ? optionsFiltered
                    //         : filter.legalEntities,
                    // exceptionListName: filter.exceptionListName,
                    sort: sort && order ? `${sort},${order}` : "",
                    size: exceptionListGridRef?.current?.api?.paginationGetPageSize() || 10,
                    page: exceptionListGridRef?.current?.api?.paginationGetCurrentPage() || 0,
                    // showOnlyActive: true,
                });
                if (exceptionListAncillaryExceptionsPaged.totalElements === 0) {
                    exceptionListGridRef.current!.api.showNoRowsOverlay();
                } else {
                    exceptionListGridRef.current!.api.hideOverlay();
                }

                params.success({
                    rowData: exceptionListAncillaryExceptionsPaged.content,
                    rowCount: exceptionListAncillaryExceptionsPaged.totalElements,
                });

                const selectedData: ExceptionListAncillaryExceptions[] =
                    exceptionListGridRef?.current?.api?.getSelectedRows() || [];
                if (selectedData?.length === 0) {
                    ancillaryExceptionGridRef?.current?.api?.setRowData([]);
                    ancillaryExceptionGridRef?.current?.api?.refreshCells();
                } else {
                    const exceptionListAncillaryExceptions: ExceptionListAncillaryExceptions =
                        selectedData[0] as ExceptionListAncillaryExceptions;
                    const ancillaryExceptions: AncillaryExceptionEntityResponse[] =
                        exceptionListAncillaryExceptions?.ancillaryExceptions || [];
                    ancillaryExceptionGridRef?.current?.api?.setRowData(
                        ancillaryExceptions
                    );
                    ancillaryExceptionGridRef?.current?.api?.refreshCells();
                }
            },
        };
    };

    const onGroupSelectionChange = useCallback(() => {
        const selectedRows: any[] =
            exceptionListGridRef.current!.api.getSelectedRows();
        const exceptionListAncillaryExceptions: ExceptionListAncillaryExceptions =
            selectedRows[0] as ExceptionListAncillaryExceptions;
        const ancillaryExceptions: AncillaryExceptionEntityResponse[] =
            exceptionListAncillaryExceptions?.ancillaryExceptions || [];
        setSelectedExceptionListAncillaryException(
            exceptionListAncillaryExceptions
        );
        ancillaryExceptionGridRef?.current?.api?.setRowData(ancillaryExceptions);
        ancillaryExceptionGridRef?.current?.api?.refreshCells();
    }, []);
    /* AG GRID - END */

    /* FORM - START */
    const validationSchema = yup
        .object()
        .shape({
            exceptionList: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            ancillaryExceptions: yup
                .array()
                .of(
                    yup.object().shape({
                        code: yup.string(),
                        label: yup.string(),
                    })
                )
                .min(1)
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            // dealer: yup
            //     .object()
            //     .shape({
            //         code: yup.string(),
            //         label: yup.string(),
            //     })
            //     .required(t("translation:validation.fieldMandatory"))
            //     .nullable(true),
            // legalEntity: yup
            //     .object()
            //     .shape({
            //         code: yup.string(),
            //         label: yup.string(),
            //     })
            //     .nullable()
            //     .required(t("translation:validation.fieldMandatory")),
        })
        .defined();

    // Use Form
    const form = useForm<FormExceptionListAncillaryExceptionModel>({
        defaultValues: {
            exceptionList: null,
            ancillaryExceptions: [],
            // dealer: null,
            // legalEntity: null,
        },

        resolver: yupResolver(
            validationSchema,
            {},
            {
                rawValues: true
            }
        ),
        shouldUnregister: true
    });
    /* FORM - END */

    /* TABLE ACTIONS - START */
    const deleteButtonClicked = (data: AncillaryExceptionEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => handleConfirmDelete(data.id),
            message: t(
                "pricing:exceptionList.dialogContents.removeExceptionListAncillaryException"
            ),
        });
    };

    const handleConfirmDelete = async (id: number) => {
        try {
            await mutationDeleteExceptionListAncillaryException.mutate(id);
            setDialog(null);
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        } catch (e) {
        }
    };

    const handleConfirmSaveExceptionListAncillaryException = async (
        formValues: FormExceptionListAncillaryExceptionModel
    ) => {
        try {
            const exceptionListAncillaryExceptionsIds: ExceptionListAncillaryExceptionAssociation[] =
                formValues.ancillaryExceptions.map(
                    (ancillaryException: AncillaryExceptionSelectFE) => {
                        return {
                            id: {
                                exceptionListId: formValues.exceptionList?.code as number,
                                ancillaryExceptionId: ancillaryException?.code as number,
                            },
                        };
                    }
                );

            await postExceptionListAncillaryExceptionAll(
                exceptionListAncillaryExceptionsIds
            );
            handleGetExceptionListAncillaryExceptionsPage();
            setAncillaryExceptions([]);
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            setFormDialog(null);
            form.reset();
            setExceptionListInUse(false);

            if (
                (selectedExceptionListAncillaryException?.exceptionList
                    ?.id as number) !== (formValues?.exceptionList?.code as number)
            ) {
                exceptionListGridRef?.current?.api?.deselectAll();
                setSelectedExceptionListAncillaryException(null);
            }
        } catch (e) {
            dispatchGlobalData(SET_SNACKBAR.ERROR);
        }
    };
    /* TABLE ACTIONS - END */

    const toolbar = {
        renderActions: () => (
            <BasicToolbar addIcon={
                !!user.profiles.find((profile: any) => {
                    return profile.auths.find((auth: any) => {
                        return auth.name === "SCOPE_PRC_CREATE_EXCEPTION_LIST_ANCILLARY_EXCEPTION";
                    });
                }) ?
                    {
                        onClick: () => {
                            setFormDialog({
                                open: true,
                                onClickConfirm: form.handleSubmit(handleConfirmSaveExceptionListAncillaryException),
                                readonly: false
                            });
                        }
                    } : undefined}
            />
        )
    };

    return (
        <Box>
            <SectionHeader title={t("translation:auths.SCOPE_PRC_VIEW_EXCEPTION_LIST_ANCILLARY_EXCEPTION.name")}/>

            {/* FORM */}
            {formDialog?.open &&
                <Dialog open={formDialog.open} maxWidth={"lg"} fullWidth>
                    <BootstrapDialogTitle id="customized-dialog-title" onClose={() => {
                        form.reset();
                        setFormDialog(null);
                        setExceptionListInUse(false);
                    }}>
                        <Box sx={{display: "flex", alignItems: "center"}}>
                            {t("pricing:exceptionList.section.titles.addAncillaryExceptionToExceptionList")}
                        </Box>
                    </BootstrapDialogTitle>
                    <DialogContent style={{color: theme.palette.primary.main, paddingTop: "15px"}}>
                        <FormProvider {...form}>
                            <FormExceptionListAncillaryException
                                exceptionListInUse={exceptionListInUse}
                                setExceptionListInUse={setExceptionListInUse}
                                readOnly={false}
                            />
                        </FormProvider>
                    </DialogContent>
                    <DialogActions style={{backgroundColor: theme.palette.neutral.bright, padding: "1rem"}}>
                        <Button variant="outlined"
                                color={"accent"}
                                onClick={() => {
                                    form.reset();
                                    setFormDialog(null);
                                    setExceptionListInUse(false);
                                }}
                        >
                            {t("pricing:actions.cancel")}
                        </Button>
                        <Button sx={{marginLeft: "16px"}}
                                autoFocus
                                color={"accent"}
                                onClick={formDialog?.onClickConfirm}
                        >
                            {t("pricing:actions.confirm")}
                        </Button>
                    </DialogActions>
                </Dialog>
            }

            <ExceptionListAncillaryExceptionFilters
                handleClickSearch={handleTriggerFiltersInitial}
                handleClickResetSearch={handleTriggerResetFilters}
                testFilters={testFilters}
                setTestFilters={setTestFilters}
            />

            <Grid container spacing={2} p={2}>
                <Grid item xs={12} md={6}>
                    <AgGridCustomTable
                        gridRef={exceptionListGridRef}
                        columnDefs={exceptionListColumnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={0}
                        toolbar={toolbar}
                        pagination
                        localeText={agGridLocale}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onSelectionChanged={onGroupSelectionChange}
                        rowSelection="single"
                        onGridReady={() => onGridReady({
                            initialSortColId: "exceptionListName",
                            gridRef: exceptionListGridRef,
                            createDatasource
                        })}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <AgGridCustomTable
                        gridRef={ancillaryExceptionGridRef}
                        rowData={selectedExceptionListAncillaryException?.ancillaryExceptions || []}
                        columnDefs={ancillaryExceptionColumnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={0}
                        toolbar={{
                            renderActions: () => (
                                <div style={{overflow: "hidden"}}>
                                    <AddIcon sx={{color: "transparent", overflow: "hidden"}}/>
                                </div>)
                        }}
                        pagination
                        localeText={agGridLocale}
                        domLayout="autoHeight"
                        paginationPageSize={10}
                    />
                </Grid>
            </Grid>

            {dialog?.open &&
                <DialogComponent
                    type={"info"}
                    onClickConfirm={dialog.onClickConfirm}
                    setOpen={() => setDialog(null)}
                    open={dialog.open}
                    message={dialog.message}
                />
            }

            <Loading/>
        </Box>
    );
};

export default ExceptionListAncillaryException;
